import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchJobs,
  fetchJobFrequencySpark,
  pauseJob,
  resumeJob,
  drainJob,
  retryFailedJob,
  type JobStatsRow,
} from '../../api/admin.js';
import { Sparkline } from '../../components/charts/Sparkline.js';
import { MutationModal } from '../../components/ui/MutationModal.js';
import { formatNumber, formatPercent, formatDurationMs, formatDateTime } from '../../lib/format.js';

type JobModalMode = null | 'pause' | 'drain' | 'retry';

export function JobsPage(): ReactElement {
  const [selected, setSelected] = useState<string | null>(null);
  const [modal, setModal] = useState<JobModalMode>(null);
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['admin', 'jobs'],
    queryFn: fetchJobs,
    refetchInterval: 15_000,
  });

  const pauseMut = useMutation({
    mutationFn: (reason: string) => pauseJob(selected ?? '', reason || undefined),
    onSuccess: () => {
      setModal(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'jobs'] });
    },
  });
  const resumeMut = useMutation({
    mutationFn: () => resumeJob(selected ?? ''),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'jobs'] });
    },
  });
  const drainMut = useMutation({
    mutationFn: () => drainJob(selected ?? ''),
    onSuccess: () => {
      setModal(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'jobs'] });
    },
  });
  const retryMut = useMutation({
    mutationFn: () => retryFailedJob(selected ?? ''),
    onSuccess: () => {
      setModal(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'jobs'] });
    },
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Jobs</h1>
          <p className="page-sub">Workers pg-boss · agregados 24h · refresh 15s</p>
        </div>
      </div>
      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {(query.error).message}</div>
      ) : (
        <>
          <section className="card">
            <header className="card-head">
              <h3 className="card-title">Queue depth (pg-boss)</h3>
            </header>
            <div className="queue-grid">
              {Object.keys(query.data.queueDepth).length === 0 ? (
                <p className="empty">Sin queues activas</p>
              ) : (
                Object.entries(query.data.queueDepth).map(([queue, states]) => (
                  <div className="queue-card" key={queue}>
                    <h4>{queue}</h4>
                    <ul>
                      {Object.entries(states).map(([state, count]) => (
                        <li key={state}>
                          <span className={`state-pill state-${state}`}>{state}</span>
                          <strong>{formatNumber(count)}</strong>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </div>
          </section>
          <section className="card">
            <header className="card-head">
              <h3 className="card-title">Job runs (24h, spans)</h3>
            </header>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Total</th>
                  <th>Errors</th>
                  <th>Error rate</th>
                  <th>p50</th>
                  <th>p95</th>
                  <th>Activity 24h</th>
                  <th>Last run</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {query.data.jobs.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="empty">Sin runs en las últimas 24h</td>
                  </tr>
                ) : (
                  query.data.jobs.map((j) => (
                    <JobRow
                      key={j.jobName}
                      job={j}
                      selected={selected === j.jobName}
                      onSelect={() => setSelected(j.jobName)}
                      onPause={() => {
                        setSelected(j.jobName);
                        setModal('pause');
                      }}
                      onResume={() => {
                        setSelected(j.jobName);
                        resumeMut.mutate();
                      }}
                      onDrain={() => {
                        setSelected(j.jobName);
                        setModal('drain');
                      }}
                      onRetry={() => {
                        setSelected(j.jobName);
                        setModal('retry');
                      }}
                    />
                  ))
                )}
              </tbody>
            </table>
          </section>
        </>
      )}

      <MutationModal
        open={modal === 'pause' && selected !== null}
        title={`Pause job — ${selected ?? ''}`}
        description="Mientras está pausado, el cron poll de pg-boss no procesa nuevos jobs de esta cola."
        confirmLabel="Pausar"
        tone="warn"
        isPending={pauseMut.isPending}
        errorMessage={pauseMut.error instanceof Error ? pauseMut.error.message : null}
        onCancel={() => setModal(null)}
        onConfirm={(reason) => pauseMut.mutate(reason)}
      />

      <MutationModal
        open={modal === 'drain' && selected !== null}
        title={`Drain job — ${selected ?? ''}`}
        description="Borra todos los jobs en estado 'created' de esta cola. Acción no reversible."
        confirmLabel="Drain"
        tone="danger"
        requiresLiteralConfirm="DRAIN"
        isPending={drainMut.isPending}
        errorMessage={drainMut.error instanceof Error ? drainMut.error.message : null}
        onCancel={() => setModal(null)}
        onConfirm={() => drainMut.mutate()}
      />

      <MutationModal
        open={modal === 'retry' && selected !== null}
        title={`Retry failed — ${selected ?? ''}`}
        description="Re-encola jobs con state='failed' creados en las últimas 24h. La ventana evita resucitar zombies viejos."
        confirmLabel="Retry"
        tone="warn"
        isPending={retryMut.isPending}
        errorMessage={retryMut.error instanceof Error ? retryMut.error.message : null}
        onCancel={() => setModal(null)}
        onConfirm={() => retryMut.mutate()}
      />
    </>
  );
}

interface JobRowProps {
  job: JobStatsRow;
  selected: boolean;
  onSelect: () => void;
  onPause: () => void;
  onResume: () => void;
  onDrain: () => void;
  onRetry: () => void;
}

function JobRow({
  job,
  selected,
  onSelect,
  onPause,
  onResume,
  onDrain,
  onRetry,
}: JobRowProps): ReactElement {
  const spark = useQuery({
    queryKey: ['admin', 'jobs', job.jobName, 'frequency-spark'],
    queryFn: () => fetchJobFrequencySpark(job.jobName),
    enabled: selected,
    staleTime: 60_000,
  });

  return (
    <tr
      onClick={onSelect}
      style={{ cursor: 'pointer', background: selected ? 'var(--surface-2)' : undefined }}
      data-testid={`job-row-${job.jobName}`}
    >
      <td>{job.jobName}</td>
      <td>{formatNumber(job.total)}</td>
      <td>{formatNumber(job.errors)}</td>
      <td className={job.errorRate > 0.05 ? 'bad' : ''}>{formatPercent(job.errorRate)}</td>
      <td>{formatDurationMs(job.p50Ms)}</td>
      <td>{formatDurationMs(job.p95Ms)}</td>
      <td>
        {selected && spark.data ? (
          <Sparkline values={spark.data.buckets.map((b) => b.runs)} />
        ) : (
          <span className="tt-tert">click row</span>
        )}
      </td>
      <td>{job.lastRunIso ? formatDateTime(job.lastRunIso) : '—'}</td>
      <td className="cell-actions" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="btn" onClick={onPause}>
          Pause
        </button>
        <button type="button" className="btn" onClick={onResume}>
          Resume
        </button>
        <button type="button" className="btn" onClick={onDrain}>
          Drain
        </button>
        <button type="button" className="btn" onClick={onRetry}>
          Retry
        </button>
      </td>
    </tr>
  );
}
